import React, { useState } from 'react'
import GoogleMapReact from 'google-map-react'

import classNames from 'classnames'
import { graphql, navigate, useStaticQuery } from 'gatsby'

const isClient = typeof window !== 'undefined'

type MarkerProps = {
  id: string
  lat: number | null
  lng: number | null
  magical: boolean | null
  text: string | null
  slug: string | null
  $hover?: boolean
  highlight?: {
    id?: string
    lat?: number | null
    lng?: number | null
  }
}

const GoogleMap = ({ highlight, show = 'all', hideSort = false }: { highlight?: MarkerProps['highlight']; show?: 'all' | 'magical'; hideSort?: boolean }) => {
  const [sorting, setSorting] = useState<string>(show ? show : 'all')

  const locations: Queries.AllLocationsQuery = useStaticQuery(graphql`
    query AllLocations {
      allContentfulLocation(filter: { title: { regex: "/^(?!.*(^dummy))/gi" } }, sort: { magicalPlacePosition: ASC }) {
        edges {
          node {
            contentful_id
            __typename
            id
            title
            slug
            magicalPlace
            magicalPlacePosition
            images {
              gatsbyImageData(formats: WEBP, quality: 75, width: 135, aspectRatio: 1.33, placeholder: BLURRED)
              title
            }
            description {
              raw
            }
            video {
              file {
                contentType
                fileName
                url
              }
            }
            coordinates {
              lon
              lat
            }
          }
        }
      }
    }
  `)

  if (!isClient) return <div>Not client</div>

  const apiKey = process.env.GATSBY_GOOGLE_MAPS_API
  const data = locations.allContentfulLocation.edges

  if (!apiKey) return <>No API-key</>

  const defaultProps = {
    center: {
      lat: highlight?.lat ? Number(highlight?.lat) : 60.618901,
      lng: highlight?.lat ? Number(highlight?.lng) : 16.771394
    },
    zoom: 14
  }

  const onMarkerClick = (hoverKey: any, childProps: any) => {
    if (childProps.slug) {
      navigate(`/plats/${childProps.slug}`)
    }
  }

  const changeSortingTo = (sort: string) => {
    switch (sort) {
      case 'magical':
        setSorting('magical')
        break
      case 'all':
        setSorting('all')
        break
      default:
        break
    }
  }

  const sortButtonClass = (btn: string) => {
    return classNames('uppercase cursor-pointer border-transparent px-2 py-1 border-b-2', {
      'border-white/50 text-white': (btn === 'magical' && sorting === 'magical') || (btn === 'all' && sorting === 'all')
    })
  }

  return (
    <div className='relative'>
      {!hideSort && (
        <div className='absolute z-50 left-1/2 -translate-x-1/2'>
          <div className='bg-[#E84F96]/90 px-10 pt-2 pb-5 rounded-b-3xl'>
            <div className='font-title uppercase text-[16px]'>Sortera</div>
            <div className='flex gap-x-10 flex-col md:flex-row font-title text-[20px]'>
              <div className='whitespace-nowrap'>
                <button onClick={() => changeSortingTo('magical')} className={sortButtonClass('magical')}>
                  Magiska Platser
                </button>
              </div>
              <div className='whitespace-nowrap'>
                <button onClick={() => changeSortingTo('all')} className={sortButtonClass('all')}>
                  Alla Platser
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className='h-[550px] md:h-[650px] lg:h-[900px] w-full'>
        <GoogleMapReact
          bootstrapURLKeys={{ key: apiKey }}
          onChildClick={onMarkerClick}
          defaultCenter={defaultProps.center}
          defaultZoom={defaultProps.zoom}
          hoverDistance={50 / 2}>
          {data.map((location, key) => {
            if ((sorting === 'magical' && location.node.magicalPlace) || sorting === 'all') {
              const markerData: MarkerProps = {
                id: location.node.contentful_id,
                lat: location.node.coordinates?.lat ?? null,
                lng: location.node.coordinates?.lon ?? null,
                magical: location.node.magicalPlace,
                text: location.node.title,
                slug: location.node.slug,
                highlight: highlight
              }
              return markerData?.lat && markerData?.lng && <Marker key={key} {...markerData} />
            }
          })}
        </GoogleMapReact>
      </div>
    </div>
  )
}

const Marker = (props: MarkerProps) => {
  const markerStyle = classNames(
    'absolute cursor-pointer border-2 -left-1/2 -top-1/2 bg-c-map-marker-bg rounded-full w-full h-full flex justify-center items-center',
    {
      'border-c-map-marker-border-regular': props.$hover !== true,
      'border-c-map-marker-border-hover': props.$hover === true
    }
  )

  const OrdinaryPlace = () => {
    return (
      <svg xmlns="http://www.w3.org/2000/svg" width="14.009" height="19.244" viewBox="0 0 14.009 19.244">
        <path id="Icon_material-location-on" data-name="Icon material-location-on" d="M14.5,3a6.869,6.869,0,0,0-7,6.736c0,5.052,7,12.509,7,12.509s7-7.457,7-12.509A6.869,6.869,0,0,0,14.5,3Zm0,9.141A2.456,2.456,0,0,1,12,9.736a2.5,2.5,0,0,1,5,0A2.456,2.456,0,0,1,14.5,12.141Z" transform="translate(-7.5 -3)" fill="#9b215a"/>
      </svg>
    )
  }

  const MagicalPlace = () => {
    return (
      <svg xmlns="http://www.w3.org/2000/svg" width="21.538" height="18.462" viewBox="0 0 21.538 18.462">
        <path id="Polygon_3" data-name="Polygon 3" d="M10.769,0l3.069,6.4,7.7.654-5.8,4.608,1.69,6.8-6.656-3.55-6.656,3.55,1.69-6.8L0,7.052,7.7,6.4Z" fill="#a9025b"/>
      </svg>
    )
  }

  return (
    <div className='relative  w-[30px] h-[30px]'>
      <div className={markerStyle}>
        <div>{props.magical ? <MagicalPlace /> : <OrdinaryPlace />}</div>
      </div>
      {(props.$hover || props.highlight?.id === props.id) && props.text && <Balloon text={props.text} />}
    </div>
  )
}

const Balloon = ({ text }: { text: string }) => {
  return (
    <div className='absolute left-4 -translate-y-1/2  flex items-center z-[9000]'>
      <svg xmlns='http://www.w3.org/2000/svg' width='22' height='30' viewBox='0 0 22 30'>
        <path id='Polygon_1' data-name='Polygon 1' d='M17,0,30,22H0Z' transform='translate(0 30) rotate(-90)' fill='#ff92b0' />
      </svg>
      <div className='whitespace-nowrap text-xl border-2 bg-white border-c-map-marker-border-regular -ml-1 py-1 px-3 rounded-xl'>{text}</div>
    </div>
  )
}

export default GoogleMap
